import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Button,
  useTheme,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Terminal, CopyAll, ErrorOutline } from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import { jsonInputSchema } from "../schema/jsonSchema";

export default function JsonPreviewModal({
  open,
  onClose,
  jsonData,
  importJson = async () => {},
  modalStyle = null,
  title = "Preview JSON",
}) {
  const theme = useTheme();

  const styles = {
    modal: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 800,
      bgcolor: "background.default",
      boxShadow: 24,
      p: 4,
      ...modalStyle,
    },
    logContainer: {
      mt: 2,
      p: 2,
      bgcolor: "background.paper",
      borderRadius: 1,
      border: `1px solid ${theme.palette.divider}`,
    },
  };

  const [error, setError] = useState(null);
  const editorRef = useRef();

  const ajv = useMemo(() => {
    const instance = new Ajv({ allErrors: true, strict: false });
    addFormats(instance);
    instance.compile(jsonInputSchema);
    return instance;
  }, []);

  useEffect(() => {
    try {
      const valid = ajv.validate(jsonInputSchema, jsonData);
      let validationError = valid
        ? null
        : ajv.errorsText(ajv.errors, { separator: "\n" });

      if (!validationError && Array.isArray(jsonData)) {
        const seen = new Set();
        let duplicate = null;
        for (const item of jsonData) {
          const coords = item?.elementConfig?.config?.coordenadas;
          if (coords) {
            const key = `${coords.lat}, ${coords.long}`;
            if (seen.has(key)) {
              duplicate = key;
              break;
            }
            seen.add(key);
          }
        }
        if (duplicate) {
          validationError = `Coordenadas duplicadas encontradas: ${duplicate}`;
        }
      }

      setError(validationError);
    } catch (e) {
      setError(e.message);
    }
  }, [jsonData, ajv]);

  const handleMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "inmemory://user.schema.json",
          fileMatch: ["*"],
          schema: jsonInputSchema,
        },
      ],
    });
    editor.getAction("editor.action.formatDocument")?.run();
    editor.layout();
  };

  const disabled = !jsonData;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-json-modal-title"
      aria-describedby="preview-json-modal-description"
    >
      <Box sx={styles.modal}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography id="preview-json-modal-title" variant="h6" component="h2">
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: 2,
            maxHeight: 350,
            overflow: "hidden",
            bgcolor: "background.paper",
            p: 0,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Editor
            key="monaco-editor"
            height="350px"
            defaultLanguage="json"
            onMount={handleMount}
            value={
              jsonData
                ? JSON.stringify(jsonData, null, 2)
                : "// Nenhum dado JSON carregado"
            }
            theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
            options={{
              readOnly: true,
              formatOnPaste: true,
              renderValidationDecorations: "on",
              automaticLayout: false,
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              formatOnType: true,
              wordWrap: "on",
              folding: true,
              lineNumbers: "on",
              automaticLayout: true,
            }}
          />
        </Box>

        {!disabled && (
          <Paper sx={styles.logContainer}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Terminal fontSize="small" />
                <Typography sx={{ fontWeight: 600 }}>Log</Typography>
              </Box>
              <IconButton>
                <CopyAll fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ my: 1 }} />
            {error && (
              <Box sx={{ display: "flex" }}>
                <ErrorOutline
                  sx={{
                    fontSize: "16px",
                    color: theme.palette.error.main,
                    mt: 0.5,
                    mr: 0.5,
                  }}
                />
                <Typography sx={{ fontSize: "14px" }}>{error}</Typography>
              </Box>
            )}
          </Paper>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
          }}
        >
          <Button variant="text" color="primary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={error}
            onClick={async () => {
              await importJson();
            }}
          >
            Importar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
